/**
 * Recovering a tab that outlived its deploy.
 *
 * Every deploy renames the hashed Vite chunks. A tab opened before the deploy
 * still asks for the old names the first time it lazy-loads something, gets a
 * 404, and the import rejects — the EmbedPDF viewer then sits on "Initializing
 * PDF engine..." forever, because its engine is one of those chunks. Vite
 * dispatches a cancelable `vite:preloadError` for exactly this, and index.html
 * is served `no-cache`, so a reload picks up the current build and its names.
 *
 * A reload that does not cure it (the chunk is genuinely missing) must not
 * loop, so a second failure inside the guard window is left to surface.
 */

/** sessionStorage key holding when this tab last reloaded for a stale chunk. */
export const STALE_CHUNK_RELOAD_KEY = 'rothology:stale-chunk-reload-at'

const LOOP_GUARD_MS = 10_000

type PreloadErrorEvent = Pick<Event, 'preventDefault'>

/** The slice of `window` this needs — narrow enough to fake without jsdom. */
export interface StaleChunkTarget {
  addEventListener: (
    type: 'vite:preloadError',
    listener: (event: PreloadErrorEvent) => void,
  ) => void
  location: Pick<Location, 'reload'>
  sessionStorage: Pick<Storage, 'getItem' | 'setItem'>
}

/** Reload unless this tab already reloaded for the same problem moments ago. */
export function shouldReloadForStaleChunk(
  lastReloadAt: number | null,
  now: number,
  windowMs: number = LOOP_GUARD_MS,
): boolean {
  return lastReloadAt === null || now - lastReloadAt >= windowMs
}

/**
 * Decides and records in one step. Without usable storage there is no way to
 * tell a first failure from a loop, so the answer is no.
 */
function claimReload(target: StaleChunkTarget): boolean {
  try {
    const stored = target.sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY)
    const parsed = stored === null ? NaN : Number(stored)
    const lastReloadAt = Number.isFinite(parsed) ? parsed : null
    const now = Date.now()
    if (!shouldReloadForStaleChunk(lastReloadAt, now)) return false
    target.sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, String(now))
    return true
  } catch {
    return false
  }
}

/**
 * Reload the page when a lazy chunk fails to load, at most once per guard
 * window. When it declines, the event is left alone so Vite rethrows the
 * original error.
 */
export function installStaleChunkReload(
  target: StaleChunkTarget = window,
): void {
  target.addEventListener('vite:preloadError', (event) => {
    if (!claimReload(target)) return
    event.preventDefault()
    target.location.reload()
  })
}
